import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";

export default function TermsOfUsePage() {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4 flex items-center gap-4">
          <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-10 object-contain" />
          <div>
            <h1 className="font-semibold text-primary text-lg">Terms of Use</h1>
            <p className="text-xs text-slate-500">Benchmark PH — IT-BPM ESG Pulse Navigator</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10">
        <Link to="/auth" className="inline-flex items-center gap-2 text-slate-500 hover:text-primary mb-8 transition-colors text-sm" data-testid="back-to-auth">
          <ArrowLeft className="w-4 h-4" />
          Back to Sign Up
        </Link>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-10 shadow-sm">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-1">Terms of Use</h1>
          <p className="text-slate-500 text-sm mb-8">IBPAP Double Materiality & ESG Data Questionnaire Platform — Managed by SusTek Pte Ltd</p>

          <div className="prose prose-slate prose-sm max-w-none space-y-6" data-testid="terms-content">
            <section>
              <h2 className="text-lg font-semibold text-primary mt-0">1. Acceptance of Terms</h2>
              <p>By accessing and using the IBPAP Double Materiality and ESG Data Questionnaire Platform ("Platform"), you agree to comply with and be bound by these Terms of Use. If you do not agree, you should not access or use the Platform. This Platform is developed and managed by SusTek Pte Ltd ("SusTek") on behalf of the IT & Business Process Association of the Philippines (IBPAP) to support ESG data collection, analysis, and reporting initiatives.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">2. Purpose of the Platform</h2>
              <p>The Platform is designed to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Facilitate the collection of ESG-related data from participating organizations</li>
                <li>Support the development of double materiality assessments, considering both financial and impact materiality dimensions</li>
                <li>Enable benchmarking, reporting, and sustainability strategy development aligned with global standards such as EFRAG, GRI, and SASB</li>
                <li>Provide AI-assisted insights and recommendations to guide sustainability decision-making</li>
              </ul>
              <p>The outputs generated are intended for advisory and informational purposes only and should not be construed as financial, legal, or regulatory advice.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">3. Eligibility and Authorized Use</h2>
              <p>You agree that:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>You are authorized by your organization to submit ESG-related data</li>
                <li>All information submitted is accurate, complete, and not misleading</li>
                <li>You will use the Platform solely for legitimate ESG reporting and assessment purposes</li>
              </ul>
              <p>Unauthorized use, including misuse of data or system access, is strictly prohibited.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">4. AI Advisory and Assist Functionality</h2>
              <p>The Platform includes AI-powered features designed to assist users in:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Interpreting ESG inputs</li>
                <li>Suggesting materiality prioritization</li>
                <li>Generating sustainability insights and recommendations</li>
              </ul>
              <h3 className="text-base font-semibold text-slate-700 mt-4">AI Technology Disclosure</h3>
              <p>The AI advisory and assist functions are powered by Gemini 3 Flash Large Language Model (LLM) developed by Google. These AI capabilities operate within a controlled environment hosted on Google Cloud Platform (GCP).</p>
              <h3 className="text-base font-semibold text-slate-700 mt-4">Limitations of AI</h3>
              <ul className="list-disc pl-5 space-y-1">
                <li>AI outputs are probabilistic and may not always be accurate, complete, or up-to-date</li>
                <li>AI-generated insights should be reviewed and validated by qualified professionals</li>
                <li>Users remain fully responsible for decisions made based on AI-assisted outputs</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">5. Technology Infrastructure</h2>
              <p>The Platform operates on:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Google Cloud Platform (GCP) infrastructure</li>
                <li>Containerized workloads deployed via Kubernetes pods for scalability, reliability, and security</li>
              </ul>
              <p>While SusTek implements industry-standard safeguards, users acknowledge that:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>No system is completely immune from risks such as cyber threats or service interruptions</li>
                <li>Platform availability may be subject to maintenance, upgrades, or unforeseen disruptions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">6. Data Usage and Ownership</h2>
              <p>Users retain ownership of the data they submit.</p>
              <p>By using the Platform, users grant IBPAP and SusTek the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Aggregate and anonymize data for industry-level insights</li>
                <li>Use data for sustainability reporting, benchmarking, and research</li>
              </ul>
              <p>No organization-specific data will be disclosed without explicit consent, unless required by law.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">7. Confidentiality and Data Protection</h2>
              <p>SusTek implements appropriate technical and organizational measures to protect submitted data, including:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Secure cloud infrastructure (GCP)</li>
                <li>Access controls and role-based permissions</li>
                <li>Encryption and monitoring mechanisms</li>
              </ul>
              <p>Users are responsible for ensuring that:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>They do not upload unauthorized or sensitive personal data beyond what is required</li>
                <li>They comply with applicable data privacy regulations (e.g., GDPR, PDPA, local laws)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">8. Intellectual Property</h2>
              <p>All Platform content, including methodologies, questionnaires, AI models and outputs (excluding user data) are the intellectual property of SusTek and/or IBPAP, unless otherwise stated.</p>
              <p>Users may not:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Copy, reproduce, or distribute Platform content without prior written consent</li>
                <li>Reverse engineer or attempt to extract underlying AI models or system architecture</li>
              </ul>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">9. Limitation of Liability</h2>
              <p>To the fullest extent permitted by law, SusTek and IBPAP shall not be liable for any direct, indirect, incidental, or consequential damages arising from:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Use or inability to use the Platform</li>
                <li>Reliance on AI-generated insights</li>
                <li>Data inaccuracies submitted by users</li>
              </ul>
              <p>Use of the Platform is at your own risk.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">10. Modifications to the Platform and Terms</h2>
              <p>SusTek and IBPAP reserve the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Update or modify the Platform, including AI functionalities</li>
                <li>Revise these Terms of Use at any time</li>
              </ul>
              <p>Continued use of the Platform constitutes acceptance of any updated Terms.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">11. Non-Indemnification, AI Output Risk, and Data Use Alignment Clause</h2>
              <p>In alignment with the Platform's data governance, security commitments, and permitted uses as defined in the Privacy Policy, users expressly acknowledge and agree to the following:</p>

              <h3 className="text-base font-semibold text-slate-700 mt-4">11.1 AI Output Nature and Data Dependency</h3>
              <p>The Platform's AI advisory and assist functionalities generate outputs based on user-submitted data, aggregated and anonymized datasets used for ESG benchmarking and analysis, and pre-trained models including the Gemini 3 Flash Large Language Model. AI outputs are data-dependent and probabilistic, and may reflect limitations, inconsistencies, or biases in underlying inputs. Outputs may contain errors, omissions, or "hallucinations", particularly where data is incomplete, subjective, or interpreted.</p>

              <h3 className="text-base font-semibold text-slate-700 mt-4">11.2 No Reliance and Mandatory Validation Obligation</h3>
              <p>Consistent with the intended use of data for aggregated ESG analysis and advisory purposes only, users agree that AI-generated outputs shall not be relied upon as definitive, verified, or authoritative information. All outputs must be independently reviewed, validated, and contextualized by the user or qualified professionals. Users are solely responsible for ensuring that any ESG disclosures, regulatory submissions, or strategic decisions meet applicable standards (e.g., EFRAG, GRI, SASB, TCFD).</p>

              <h3 className="text-base font-semibold text-slate-700 mt-4">11.3 No Indemnification for AI-Related Outcomes</h3>
              <p>To the fullest extent permitted by law, SusTek Pte Ltd and IBPAP shall have no obligation to indemnify, defend, or hold harmless any user, organization, or third party for any claims arising from AI-generated outputs, recommendations, or interpretations; hallucinations, inaccuracies, or incomplete ESG assessments; misuse, misinterpretation, or over-reliance on AI outputs; or decisions made based on aggregated or anonymized industry insights.</p>

              <h3 className="text-base font-semibold text-slate-700 mt-4">11.4 Allocation of Responsibility for Submitted Data</h3>
              <p>Users remain fully accountable for the accuracy, completeness, and lawfulness of submitted data. SusTek acts strictly as a data processor, and IBPAP as data controller, without assuming responsibility for user-provided inaccuracies. Any AI output derived from inaccurate or incomplete inputs shall not give rise to liability on the part of SusTek or IBPAP.</p>

              <h3 className="text-base font-semibold text-slate-700 mt-4">11.5 Waiver of Claims and Limitation of Recourse</h3>
              <p>Users hereby irrevocably waive and release any and all claims against SusTek and IBPAP arising from AI-generated outputs and agree that no legal, financial, or regulatory recourse shall arise from use of AI-assisted ESG insights, inclusion of such outputs in internal or external reports, or any downstream reliance by third parties.</p>

              <h3 className="text-base font-semibold text-slate-700 mt-4">11.6 No Warranty of Compliance or Fitness</h3>
              <p>No warranties are provided that AI outputs meet regulatory or audit requirements, are suitable for ESG disclosures or compliance submissions, or accurately reflect organizational or industry-level performance. All outputs are provided "as-is" without warranty of any kind, whether express or implied.</p>

              <h3 className="text-base font-semibold text-slate-700 mt-4">11.7 Cross-Border and Cloud Processing Acknowledgement</h3>
              <p>Users acknowledge that AI processing and data handling may occur within Google Cloud Platform (GCP) environments across jurisdictions. Outputs generated may be influenced by distributed processing systems and model behaviors across regions. Such architecture aligns with the Privacy Policy's provisions on cross-border data transfers and secure infrastructure safeguards.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">12. Governing Law</h2>
              <p>These Terms shall be governed by and construed in accordance with applicable laws of the Philippines (for IBPAP context) and relevant international data protection and ESG regulatory frameworks.</p>
            </section>

            <section>
              <h2 className="text-lg font-semibold text-primary">13. Contact and Support</h2>
              <p>For inquiries regarding the Platform, Terms of Use, or ESG program:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>IBPAP ESG Program Team — <a href="mailto:esgsupport@ibpap.org" className="text-accent hover:underline">esgsupport@ibpap.org</a></li>
                <li>SusTek Pte Ltd Support Team — <a href="mailto:support@sustek.ai" className="text-accent hover:underline">support@sustek.ai</a></li>
              </ul>
            </section>
          </div>
        </div>
      </main>

      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white">
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 hover:underline">
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
